import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import {
    Box, Typography, Avatar, Chip, Button, CircularProgress,
    Tabs, Tab, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedIcon from '@mui/icons-material/Verified';
import moment from 'jalali-moment';
import { glass } from '../theme';
import { Reveal } from '../components/Reveal';

function ProviderRow({ p, onVerify, busyId }) {
    return (
        <Box sx={{ ...glass.card, p: 2, display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 800 }}>
                {(p.full_name || p.phone_number || 'ه')[0]}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 700 }}>{p.full_name || 'بدون نام ثبت‌شده'}</Typography>
                    {p.is_verified_by_admin && <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {p.phone_number} · کد ملی: {p.national_code || '—'} · {p.work_experience_years || 0} سال سابقه
                </Typography>
                {p.specialties && <Typography variant="body2" color="text.secondary">تخصص: {p.specialties}</Typography>}
                <Typography variant="caption" color="text.disabled">
                    ثبت‌نام: {p.date_joined ? moment(p.date_joined).locale('fa').format('D MMMM YYYY') : '—'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                {!p.is_verified_by_admin ? (
                    <Button
                        size="small" variant="contained" color="primary" startIcon={<CheckCircleIcon />}
                        disabled={busyId === p.id}
                        onClick={() => onVerify(p.id, true)}
                    >
                        تایید
                    </Button>
                ) : (
                    <Button
                        size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                        disabled={busyId === p.id}
                        onClick={() => onVerify(p.id, false)}
                    >
                        لغو تایید
                    </Button>
                )}
            </Box>
        </Box>
    );
}

function AdminProviders() {
    const [tab, setTab] = useState(0);
    const [pending, setPending] = useState([]);
    const [all, setAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState('');

    const load = () => {
        setLoading(true);
        Promise.all([
            apiClient.get('/api/admin-panel/providers/pending/'),
            apiClient.get('/api/admin-panel/providers/'),
        ])
            .then(([pendingRes, allRes]) => {
                setPending(pendingRes.data);
                setAll(allRes.data);
            })
            .catch(() => setError('خطا در دریافت لیست همیاران.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleVerify = async (profileId, verified) => {
        setBusyId(profileId);
        try {
            await apiClient.post(`/api/admin-panel/providers/${profileId}/verify/`, { verified });
            load();
        } catch {
            setError('خطا در ثبت تغییر وضعیت تایید.');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    const list = tab === 0 ? pending : all;

    return (
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>تایید همیاران</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                تایید هویت و سابقه‌ی همیاران برای افزایش اعتماد خانواده‌ها.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label={`در انتظار تایید (${pending.length.toLocaleString('fa-IR')})`} />
                <Tab label={`همه‌ی همیاران (${all.length.toLocaleString('fa-IR')})`} />
            </Tabs>

            {list.length === 0 ? (
                <Box sx={{ ...glass.card, p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">موردی برای نمایش وجود ندارد.</Typography>
                </Box>
            ) : (
                list.map((p, i) => (
                    <Reveal key={p.id} delay={Math.min(i * 0.05, 0.4)} y={12}>
                        <ProviderRow p={p} onVerify={handleVerify} busyId={busyId} />
                    </Reveal>
                ))
            )}
        </Box>
    );
}

export default AdminProviders;
